import { shareCopy } from '#src/app/shared/share/copy';
import { shareEmail } from '#src/app/shared/share/email';
import { shareFacebook } from '#src/app/shared/share/facebook';
import { shareFacebookMessenger } from '#src/app/shared/share/facebookMessenger';
import { shareLine } from '#src/app/shared/share/line';
import { shareNative } from '#src/app/shared/share/mobileNativeShare';
import { shareNextdoor } from '#src/app/shared/share/nextdoor';
import { shareReddit } from '#src/app/shared/share/reddit';
import { shareSms } from '#src/app/shared/share/sms';
import { shareTwitter } from '#src/app/shared/share/twitter';
import { shareVk } from '#src/app/shared/share/vk';
import { shareWhatsapp } from '#src/app/shared/share/whatsapp';

import type { ShareChannel } from '../../../shared/types';

import { InstagramShare } from './instagram';
import { InstagramStoriesShare } from './instagramStories';
import { TiktokShare } from './tiktok';
import type { ShareActionComponentPropsWithOptions, ShareActionConfig } from './types';

export type { ShareActionComponentPropsWithOptions };

const SHARE_ACTIONS: Readonly<Record<ShareChannel, ShareActionConfig>> = {
	facebook: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName, config: { facebook } }, { utilityContext, track }) =>
			shareFacebook(
				url,
				{
					trackingData,
					trackingEvents: {
						success: `${trackingEventName}_success`,
						intent: `${trackingEventName}_intent`,
						legacy_success: trackingEventName,
						cancel: `${trackingEventName}_cancel`,
						failure: `${trackingEventName}_failure`,
					},
					mode: facebook?.mode,
				},
				utilityContext,
				track,
			),
	},
	facebookMessenger: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareFacebookMessenger(
				url,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				utilityContext,
				track,
			);
			return Promise.resolve(true);
		},
	},
	twitter: {
		type: 'function',
		action: async (
			{ url, content: { title }, trackingData, trackingEventName },
			{ track, utilityContext, countryCode, locale },
		) => {
			shareTwitter({
				url,
				comment: title,
				options: { trackingData, trackingEvents: { success: trackingEventName }, countryCode, locale },
				track,
				utilityContext,
			});
			return Promise.resolve(true);
		},
	},
	whatsapp: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareWhatsapp(
				title,
				url,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	reddit: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			await shareReddit(
				title,
				url,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
				utilityContext.i18n,
			);
			return Promise.resolve(true);
		},
	},
	vk: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareVk(
				url,
				title,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	line: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			await shareLine(
				url,
				title,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
				utilityContext.i18n,
			);
			return Promise.resolve(true);
		},
	},
	email: {
		type: 'function',
		action: async ({ content: { title, body }, trackingData, trackingEventName }, { track }) => {
			shareEmail(
				title,
				body,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	sms: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { utilityContext, track }) => {
			shareSms(
				url,
				title,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				utilityContext,
				track,
			);
			return Promise.resolve(true);
		},
	},
	instagram: {
		type: 'component',
		Component: InstagramShare,
	},
	instagramStories: {
		type: 'component',
		Component: InstagramStoriesShare,
	},
	tiktok: {
		type: 'component',
		Component: TiktokShare,
	},
	nextdoor: {
		type: 'function',
		action: async ({ url, content: { title }, trackingData, trackingEventName }, { track }) => {
			shareNextdoor(
				url,
				title,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
					},
				},
				track,
			);
			return Promise.resolve(true);
		},
	},
	copy: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName }, { utilityContext, track }) =>
			shareCopy(
				url,
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
						failure: `${trackingEventName}_failure`,
					},
				},
				track,
				utilityContext.i18n,
			),
	},
	mobileNativeShare: {
		type: 'function',
		action: async ({ url, trackingData, trackingEventName }, { track }) =>
			shareNative(
				{ url },
				{
					trackingData,
					trackingEvents: {
						success: trackingEventName,
						failure: `${trackingEventName}_failure`,
					},
				},
				track,
			),
	},
};

export function getShareActionConfig(channel: ShareChannel): ShareActionConfig {
	return SHARE_ACTIONS[channel];
}
